import * as React from 'react';
import { motion } from 'framer-motion';
import cntl from 'cntl';
import SeparatorBar from './SeparatorBar';
import NutrientRow from './NutrientRow';

// FDA Daily Values based on a standard 2,000 calorie diet
const STANDARD_DV = {
  totalFat: 78,
  saturatedFat: 20,
  cholesterol: 300,
  sodium: 2300,
  totalCarbohydrate: 275,
  dietaryFiber: 28,
  sugars: 50, // Added sugars FDA target
};

// Container styling strictly enforcing the Helvetica/Arial FDA look
const labelContainerCn = cntl`
  font-['Helvetica_Neue',Helvetica,Arial,sans-serif] 
  p-2
  border-[2px] 
  border-black 
  bg-white 
  text-black 
  w-full
  max-w-[320px]
  flex 
  flex-col
`;

interface NutritionLabelProps {
  backgroundColor?: string;
  servingSize: string;
  servingsPerContainer: number;
  calories: number;
  caloriesFromFat?: number; // Kept for backwards compatibility, though modern FDA dropped this
  totalFat: number;
  saturatedFat: number;
  transFat: number;
  cholesterol: number;
  sodium: number;
  totalCarbohydrate: number;
  dietaryFiber: number;
  sugars: number;
  protein: number;
  vitamins?: Array<string>;
}

const NutritionLabel: React.FC<NutritionLabelProps> = (props) => {
  const bg = props.backgroundColor || '#ffffff';

  // Helper to calculate percentages securely
  const calcDV = (amount: number, target: number) =>
    Math.round((amount / target) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={labelContainerCn}
      style={{ backgroundColor: bg }}
    >
      {/* Header Section */}
      <h1 className="text-4xl !text-black !font-black tracking-tighter m-0 leading-none mb-1">
        Nutrition Facts
      </h1>
      <div className="flex justify-between items-end border-t border-black pt-1">
        <h2 className="text-[15px] !text-black font-normal m-0 leading-tight">
          {props.servingsPerContainer} servings per container
        </h2>
      </div>
      <div className="flex justify-between items-end mb-1">
        <h2 className="text-[15px] !text-black !font-black m-0 leading-tight">
          Serving size
        </h2>
        <h2 className="text-[15px] !text-black !font-black m-0 leading-tight">
          {props.servingSize}
        </h2>
      </div>

      <SeparatorBar thickness="thick" />

      {/* Calories Section */}
      <div className="flex justify-between items-end py-1">
        <div className="flex flex-col">
          <span className="text-[13px] !font-black leading-none">
            Amount Per Serving
          </span>
          <span className="text-3xl !font-black leading-none mt-1">
            Calories
          </span>
        </div>
        <span className="text-4xl !font-black leading-none">
          {props.calories}
        </span>
      </div>

      <SeparatorBar thickness="medium" />

      {/* Daily Value Header */}
      <div className="text-right text-[11px] !font-black py-1">
        % Daily Value*
      </div>

      {/* Nutrients */}
      <NutrientRow
        label="Total Fat"
        value={`${props.totalFat}g`}
        percentage={calcDV(props.totalFat, STANDARD_DV.totalFat)}
        boldLabel
      />
      <NutrientRow
        label="Saturated Fat"
        value={`${props.saturatedFat}g`}
        percentage={calcDV(props.saturatedFat, STANDARD_DV.saturatedFat)}
        indent
      />
      <NutrientRow label="Trans Fat" value={`${props.transFat}g`} indent />
      <NutrientRow
        label="Cholesterol"
        value={`${props.cholesterol}mg`} // Note: Changed to mg per FDA specs
        percentage={calcDV(props.cholesterol, STANDARD_DV.cholesterol)}
        boldLabel
      />
      <NutrientRow
        label="Sodium"
        value={`${props.sodium}mg`}
        percentage={calcDV(props.sodium, STANDARD_DV.sodium)}
        boldLabel
      />
      <NutrientRow
        label="Total Carbohydrate"
        value={`${props.totalCarbohydrate}g`} // Note: Changed to g per FDA specs
        percentage={calcDV(
          props.totalCarbohydrate,
          STANDARD_DV.totalCarbohydrate,
        )}
        boldLabel
      />
      <NutrientRow
        label="Dietary Fiber"
        value={`${props.dietaryFiber}g`}
        percentage={calcDV(props.dietaryFiber, STANDARD_DV.dietaryFiber)}
        indent
      />
      <NutrientRow label="Total Sugars" value={`${props.sugars}g`} indent />
      <NutrientRow label="Protein" value={`${props.protein}g`} boldLabel />

      <SeparatorBar thickness="thick" className="mt-1" />

      {/* Vitamins Loop */}
      {props.vitamins &&
        props.vitamins.map((vitamin, i) => (
          <NutrientRow key={i} noBorder={i === 0} label={vitamin} />
        ))}

      {/* Footnote */}
      <div className="border-t border-black mt-1 pt-1">
        <p className="text-[10px] leading-tight text-gray-700 m-0">
          * The % Daily Value (DV) tells you how much a nutrient in a serving of
          food contributes to a daily diet. 2,000 calories a day is used for
          general nutrition advice.
        </p>
      </div>
    </motion.div>
  );
};

export default NutritionLabel;
